import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useTranslation } from 'react-i18next';
import { useSupplier } from '@/hooks/use-suppliers';
import { usePurchaseDocuments } from '@/hooks/use-purchases';
import { usePayments } from '@/hooks/use-payments';
import { Card, CardContent } from '@erp/ui';
import { Badge } from '@erp/ui';
import { Button } from '@erp/ui';
import { Separator } from '@erp/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@erp/ui';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { ArrowRight, Pencil } from '@/components/icons';
import { SupplierFormDialog } from './SupplierFormDialog';

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
function formatDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  order: 'purchases.order', reception: 'purchases.reception',
  invoice: 'purchases.invoice', credit_note: 'purchases.creditNote',
};
const DOC_PATHS: Record<string, string> = {
  order: '/purchases/orders', reception: '/purchases/receptions',
  invoice: '/purchases/invoices', credit_note: '/purchases/credit-notes',
};
const DOC_TYPES_ORDER = ['order', 'reception', 'invoice', 'credit_note'] as const;

export function SupplierProfilePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSmartNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [docFilter, setDocFilter] = useState<string>('all');
  const { data: supplier, isLoading } = useSupplier(id ?? '');
  const { data: purchaseDocs } = usePurchaseDocuments({ supplierId: id });
  const { data: payments } = usePayments({ thirdPartyType: 'supplier', thirdPartyId: id });

  const stats = useMemo(() => {
    const docs = purchaseDocs ?? [];
    const counts = { order: 0, reception: 0, invoice: 0, credit_note: 0 } as Record<string, number>;
    for (const d of docs) counts[d.docType] = (counts[d.docType] ?? 0) + 1;
    const totalInvoiced = docs.filter((d) => d.docType === 'invoice').reduce((s, d) => s + d.totalTtc, 0);
    const totalCredited = docs.filter((d) => d.docType === 'credit_note').reduce((s, d) => s + d.totalTtc, 0);
    const totalPaid = (payments ?? []).reduce((s, p) => s + p.amount, 0);
    return {
      counts,
      totalInvoiced, totalCredited, totalPaid,
      balance: totalInvoiced - totalCredited - totalPaid,
    };
  }, [purchaseDocs, payments]);

  const filteredDocs = useMemo(() => {
    if (!purchaseDocs) return [];
    if (docFilter === 'all') return purchaseDocs;
    return purchaseDocs.filter((d) => d.docType === docFilter);
  }, [purchaseDocs, docFilter]);

  if (isLoading && !supplier) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }
  if (!supplier) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.noResults')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold truncate">{supplier.name}</h2>
              <Badge variant="outline">{supplier.type === 'company' ? t('suppliers.typeCompany') : t('suppliers.typeIndividual')}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {supplier.code}
              {supplier.city && <> · {supplier.city}</>}
              {supplier.phone && <> · {supplier.phone}</>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil size={14} className="me-1.5" />
              {t('common.edit')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/suppliers')}>
              {t('common.back')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <Kpi label={t('suppliers.profile.totalInvoiced')} value={`${formatPrice(stats.totalInvoiced)} DT`} />
          <Kpi label={t('suppliers.profile.totalPaid')} value={`${formatPrice(stats.totalPaid)} DT`} />
          <Kpi label={t('suppliers.profile.totalCredited')} value={`${formatPrice(stats.totalCredited)} DT`} />
          <Kpi label={t('suppliers.profile.balance')} value={`${formatPrice(stats.balance)} DT`} positive={stats.balance <= 0} />
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">{t('suppliers.profile.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="documents">{t('suppliers.profile.tabs.documents')} ({purchaseDocs?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="payments">{t('suppliers.profile.tabs.payments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-5 space-y-3">
                <h3 className="text-sm font-semibold">{t('suppliers.profile.info')}</h3>
                <div className="space-y-2 text-sm">
                  {supplier.taxId && <InfoRow label={t('suppliers.taxId')} value={supplier.taxId} />}
                  {supplier.address && <InfoRow label={t('suppliers.address')} value={supplier.address} />}
                  {supplier.city && <InfoRow label={t('suppliers.city')} value={supplier.city} />}
                  {supplier.phone && <InfoRow label={t('suppliers.phone')} value={supplier.phone} />}
                  {supplier.email && <InfoRow label={t('suppliers.email')} value={supplier.email} />}
                  {supplier.notes && (
                    <>
                      <Separator />
                      <p className="text-muted-foreground whitespace-pre-line">{supplier.notes}</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-5 space-y-3">
                <h3 className="text-sm font-semibold">{t('suppliers.profile.documentSummary')}</h3>
                <div className="space-y-2 text-sm">
                  <CountRow label={t('purchases.orders')} count={stats.counts.order ?? 0} />
                  <CountRow label={t('purchases.receptions')} count={stats.counts.reception ?? 0} />
                  <CountRow label={t('purchases.invoices')} count={stats.counts.invoice ?? 0} />
                  <CountRow label={t('purchases.creditNotes')} count={stats.counts.credit_note ?? 0} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <FilterChip active={docFilter === 'all'} onClick={() => setDocFilter('all')}>
                  {t('common.all')} ({purchaseDocs?.length ?? 0})
                </FilterChip>
                {DOC_TYPES_ORDER.map((type) => (
                  <FilterChip key={type} active={docFilter === type} onClick={() => setDocFilter(type)}>
                    {t(DOC_TYPE_LABELS[type]!)} ({stats.counts[type] ?? 0})
                  </FilterChip>
                ))}
              </div>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.type')}</TableHead>
                      <TableHead>{t('sales.number')}</TableHead>
                      <TableHead>{t('sales.docDate')}</TableHead>
                      <TableHead className="text-end">{t('sales.totalTtc')}</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!filteredDocs.length ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">{t('common.noResults')}</TableCell></TableRow>
                    ) : filteredDocs.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell><Badge variant="outline">{t(DOC_TYPE_LABELS[doc.docType] ?? doc.docType)}</Badge></TableCell>
                        <TableCell className="font-medium">{doc.number}</TableCell>
                        <TableCell>{formatDate(doc.docDate)}</TableCell>
                        <TableCell className="text-end">{formatPrice(doc.totalTtc)} DT</TableCell>
                        <TableCell>
                          <Link to={`${DOC_PATHS[doc.docType]}/${doc.id}`}>
                            <Button variant="ghost" size="icon-xs"><ArrowRight size={14} className="rtl:rotate-180" /></Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Kpi label={t('suppliers.profile.totalInvoiced')} value={`${formatPrice(stats.totalInvoiced)} DT`} />
              <Kpi label={t('suppliers.profile.totalPaid')} value={`${formatPrice(stats.totalPaid)} DT`} />
              <Kpi label={t('suppliers.profile.balance')} value={`${formatPrice(stats.balance)} DT`} positive={stats.balance <= 0} />
            </div>
            <Card>
              <CardContent className="pt-5">
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('payments.number')}</TableHead>
                        <TableHead>{t('payments.method')}</TableHead>
                        <TableHead>{t('payments.date')}</TableHead>
                        <TableHead className="text-end">{t('payments.amount')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!payments?.length ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">{t('common.noResults')}</TableCell></TableRow>
                      ) : payments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.number}</TableCell>
                          <TableCell>{t(`payments.${p.paymentMethod}`)}</TableCell>
                          <TableCell>{formatDate(p.paymentDate)}</TableCell>
                          <TableCell className="text-end">{formatPrice(p.amount)} DT</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <SupplierFormDialog open={editOpen} onOpenChange={setEditOpen} supplier={supplier} />
    </div>
  );
}

function Kpi({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  const tone = positive === false ? 'text-destructive' : positive === true ? 'text-green-600' : '';
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-base font-bold leading-tight mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-end">{value}</span>
    </div>
  );
}

function CountRow({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{count}</span>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Button
      type="button"
      variant={active ? 'default' : 'outline'}
      size="xs"
      onClick={onClick}
      className="rounded-full"
    >
      {children}
    </Button>
  );
}
